import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getWsClient } from '@config/wsClient';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { UserRoles } from '@models/user.model';
import OrganizationEventGraphQL from '@graphql/organizationEvent.queries';
import { PENDING_INCIDENTS_QUERY_KEY } from './incident/usePendingIncidents';
import { PENDING_BUDGET_REQUESTS_QUERY_KEY } from './budget/usePendingBudgetRequests';
import { PENDING_INVOICES_COUNT_QUERY_KEY } from './activity/usePendingInvoicesCount';
import { PENDING_APPROVALS_QUERY_KEY } from './task/usePendingApprovals';
import { TASK_QUERY_KEY } from './task/useTask';
import { TASKS_QUERY_KEY } from './task/useTasks';
import { PROJECT_QUERY_KEY } from './projects/useProject';
import { PROJECTS_QUERY_KEY } from './projects/useProjects';

/**
 * Shared prefix of every activity query key in the app — see
 * `hooks/api/activity/*` and the ProjectFeed hook. React Query matches by
 * prefix, so invalidating this refreshes all of them.
 */
const ACTIVITIES_QUERY_KEY = 'activities';

/**
 * Which cached queries each entity backs. Adding a row here is all it takes to
 * put another collection on the channel — the server side is already generic.
 *
 * Keys are matched by prefix, so `'activities'` covers every activity view at
 * once: the project feed, by-project, by-task, by-organization and the
 * incident-specific lists all key off it.
 */
const QUERY_KEYS_BY_ENTITY: Record<string, string[]> = {
  INCIDENT: [PENDING_INCIDENTS_QUERY_KEY],
  BUDGET_REQUEST: [PENDING_BUDGET_REQUESTS_QUERY_KEY],
  INVOICE_ACTIVITY: [PENDING_INVOICES_COUNT_QUERY_KEY],
  ACTIVITY: [ACTIVITIES_QUERY_KEY],
  // Sprint 12, story-054/055 (U11/N2).
  TASK_APPROVAL: [PENDING_APPROVALS_QUERY_KEY],
  // Sprint 39, story-210: any task row change (own org or the other side of a delegation).
  // Project cards/pages derive progress and counts from tasks, so project keys go too.
  TASK: [TASK_QUERY_KEY, TASKS_QUERY_KEY, PROJECT_QUERY_KEY, PROJECTS_QUERY_KEY],
};

interface OrganizationEvent {
  entity: string;
  action: string;
  entityId: string;
  organizationId: string;
}

/**
 * Keeps the sidebar and dashboard counters live over a WebSocket instead of
 * polling three endpoints every three seconds.
 *
 * On each event we only *invalidate* — we never write server data into the
 * cache from the socket. That is the whole point of the signal design: the
 * refetch runs the normal HTTP query, so this user's own role, organization and
 * arguments are applied by the existing guard. Nothing sensitive travels over
 * the socket, and a count that depends on the caller's own arguments (the
 * pending-invoice count is per project list) still comes out right.
 *
 * Mount once, high in the authenticated tree.
 */
export const useOrganizationEventsSubscription = () => {
  const queryClient = useQueryClient();
  const { organizationId } = useAppSelector((state: RootState) => state.organization);
  const userRole = useAppSelector((state: RootState) => state.user.role);
  const isSuperAdmin = userRole === UserRoles.SUPER_ADMIN;

  useEffect(() => {
    // SUPER_ADMIN has no organization context and the underlying queries are
    // disabled for them, so there is nothing to keep fresh.
    if (!organizationId || isSuperAdmin) {
      return;
    }

    const unsubscribe = getWsClient().subscribe<{
      organizationEvents: OrganizationEvent;
    }>(
      {
        query: OrganizationEventGraphQL.subscriptions.organizationEvents,
        variables: { organizationId },
      },
      {
        next: ({ data }) => {
          const event = data?.organizationEvents;
          if (!event) {
            return;
          }

          const queryKeys = QUERY_KEYS_BY_ENTITY[event.entity];
          // An entity this client does not know about yet — a newer backend can
          // add one without breaking older clients.
          if (!queryKeys) {
            return;
          }

          queryKeys.forEach((queryKey) => {
            void queryClient.invalidateQueries({ queryKey: [queryKey] });
          });
        },
        error: (err) => {
          // Non-fatal. The queries keep their focus/mount/reconnect refetches,
          // so a dead socket degrades to "updates on interaction" rather than
          // to stale-forever.
          // eslint-disable-next-line no-console
          console.warn('organizationEvents subscription error', err);
        },
        complete: () => undefined,
      },
    );

    return () => {
      unsubscribe();
    };
  }, [organizationId, isSuperAdmin, queryClient]);
};

export default useOrganizationEventsSubscription;
